import React, { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react';
import type { ApiError } from '../types';

// Global app state interface
interface AppState {
  // UI state
  isLoading: boolean;
  isSidebarOpen: boolean;
  currentPage: string;
  
  // Network state
  isOnline: boolean;
  lastSyncTime: Date | null;
  
  // Error state
  globalError: ApiError | null;
  
  // Background tasks
  backgroundTasks: BackgroundTask[];
  
  // Cache state
  cacheStatus: {
    isHydrated: boolean;
    lastClearTime: Date | null;
  };
}

interface BackgroundTask {
  id: string;
  type: 'sync' | 'export' | 'import' | 'cleanup';
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  startTime: Date;
  endTime?: Date;
}

// App state actions
type AppStateAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_OPEN'; payload: boolean }
  | { type: 'SET_CURRENT_PAGE'; payload: string }
  | { type: 'SET_ONLINE_STATUS'; payload: boolean }
  | { type: 'SET_LAST_SYNC_TIME'; payload: Date }
  | { type: 'SET_GLOBAL_ERROR'; payload: ApiError | null }
  | { type: 'CLEAR_GLOBAL_ERROR' }
  | { type: 'ADD_BACKGROUND_TASK'; payload: Omit<BackgroundTask, 'id' | 'startTime'> }
  | { type: 'UPDATE_BACKGROUND_TASK'; payload: { id: string; updates: Partial<BackgroundTask> } }
  | { type: 'REMOVE_BACKGROUND_TASK'; payload: string }
  | { type: 'CLEAR_COMPLETED_TASKS' }
  | { type: 'SET_CACHE_HYDRATED'; payload: boolean }
  | { type: 'SET_CACHE_CLEARED'; payload: Date };

// Initial state
const initialState: AppState = {
  isLoading: false,
  isSidebarOpen: true,
  currentPage: 'dashboard',
  isOnline: navigator.onLine,
  lastSyncTime: null,
  globalError: null,
  backgroundTasks: [],
  cacheStatus: {
    isHydrated: false,
    lastClearTime: null,
  },
};

// App state reducer
function appStateReducer(state: AppState, action: AppStateAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        isSidebarOpen: !state.isSidebarOpen,
      };
    
    case 'SET_SIDEBAR_OPEN':
      return {
        ...state,
        isSidebarOpen: action.payload,
      };
    
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };
    
    case 'SET_ONLINE_STATUS':
      return {
        ...state,
        isOnline: action.payload,
      };
    
    case 'SET_LAST_SYNC_TIME':
      return {
        ...state,
        lastSyncTime: action.payload,
      };
    
    case 'SET_GLOBAL_ERROR':
      return {
        ...state,
        globalError: action.payload,
      };
    
    case 'CLEAR_GLOBAL_ERROR':
      return {
        ...state,
        globalError: null,
      };
    
    case 'ADD_BACKGROUND_TASK':
      const newTask: BackgroundTask = {
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        startTime: new Date(),
      };
      return {
        ...state,
        backgroundTasks: [...state.backgroundTasks, newTask],
      };
    
    case 'UPDATE_BACKGROUND_TASK':
      return {
        ...state,
        backgroundTasks: state.backgroundTasks.map(task =>
          task.id === action.payload.id
            ? { ...task, ...action.payload.updates }
            : task
        ),
      };
    
    case 'REMOVE_BACKGROUND_TASK':
      return {
        ...state,
        backgroundTasks: state.backgroundTasks.filter(task => task.id !== action.payload),
      };
    
    case 'CLEAR_COMPLETED_TASKS':
      return {
        ...state,
        backgroundTasks: state.backgroundTasks.filter(
          task => task.status !== 'completed' && task.status !== 'failed'
        ),
      };
    
    case 'SET_CACHE_HYDRATED':
      return {
        ...state,
        cacheStatus: {
          ...state.cacheStatus,
          isHydrated: action.payload,
        },
      };
    
    case 'SET_CACHE_CLEARED':
      return {
        ...state,
        cacheStatus: {
          ...state.cacheStatus,
          lastClearTime: action.payload,
        },
      };
    
    default:
      return state;
  }
}

// Context type
interface AppStateContextType {
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  
  // Convenience methods
  setLoading: (loading: boolean) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setCurrentPage: (page: string) => void;
  setGlobalError: (error: ApiError | null) => void;
  clearGlobalError: () => void;
  addBackgroundTask: (task: Omit<BackgroundTask, 'id' | 'startTime'>) => string;
  updateBackgroundTask: (id: string, updates: Partial<BackgroundTask>) => void;
  removeBackgroundTask: (id: string) => void;
  clearCompletedTasks: () => void;
}

// Create context
const AppStateContext = createContext<AppStateContextType | undefined>(undefined);

// Provider props
interface AppStateProviderProps {
  children: ReactNode;
}

// App state provider component
export function AppStateProvider({ children }: AppStateProviderProps) {
  const [state, dispatch] = useReducer(appStateReducer, initialState);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      dispatch({ type: 'SET_ONLINE_STATUS', payload: true });
      dispatch({ type: 'SET_LAST_SYNC_TIME', payload: new Date() });
    };

    const handleOffline = () => {
      dispatch({ type: 'SET_ONLINE_STATUS', payload: false });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Global error handling
  useEffect(() => {
    const handleGlobalError = (event: CustomEvent<ApiError>) => {
      dispatch({ type: 'SET_GLOBAL_ERROR', payload: event.detail });
    };

    window.addEventListener('global-error', handleGlobalError as EventListener);

    return () => {
      window.removeEventListener('global-error', handleGlobalError as EventListener);
    };
  }, []);

  // Auto-clear completed tasks after 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const completedTasks = state.backgroundTasks.filter(
        task => (task.status === 'completed' || task.status === 'failed') &&
                task.endTime &&
                now.getTime() - task.endTime.getTime() > 5 * 60 * 1000 // 5 minutes
      );

      if (completedTasks.length > 0) {
        dispatch({ type: 'CLEAR_COMPLETED_TASKS' });
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [state.backgroundTasks]);

  // Convenience methods
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const toggleSidebar = () => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  };

  const setSidebarOpen = (open: boolean) => {
    dispatch({ type: 'SET_SIDEBAR_OPEN', payload: open });
  };

  const setCurrentPage = (page: string) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
  };

  const setGlobalError = (error: ApiError | null) => {
    dispatch({ type: 'SET_GLOBAL_ERROR', payload: error });
  };

  const clearGlobalError = () => {
    dispatch({ type: 'CLEAR_GLOBAL_ERROR' });
  };

  const addBackgroundTask = (task: Omit<BackgroundTask, 'id' | 'startTime'>): string => {
    const taskId = Math.random().toString(36).substr(2, 9);
    dispatch({ 
      type: 'ADD_BACKGROUND_TASK', 
      payload: task
    });
    return taskId;
  };

  const updateBackgroundTask = (id: string, updates: Partial<BackgroundTask>) => {
    dispatch({ type: 'UPDATE_BACKGROUND_TASK', payload: { id, updates } });
  };

  const removeBackgroundTask = (id: string) => {
    dispatch({ type: 'REMOVE_BACKGROUND_TASK', payload: id });
  };

  const clearCompletedTasks = () => {
    dispatch({ type: 'CLEAR_COMPLETED_TASKS' });
  };

  // Context value
  const contextValue: AppStateContextType = {
    state,
    dispatch,
    setLoading,
    toggleSidebar,
    setSidebarOpen,
    setCurrentPage,
    setGlobalError,
    clearGlobalError,
    addBackgroundTask,
    updateBackgroundTask,
    removeBackgroundTask,
    clearCompletedTasks,
  };

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
}

// Custom hook to use app state context
export function useAppState(): AppStateContextType {
  const context = useContext(AppStateContext);
  
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  
  return context;
}

// Export types for use in other components
export type { AppState, BackgroundTask, AppStateAction };
export default AppStateContext;